import React, { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Box } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "../../styles/variables.scss";
import datePickerIcon from "../../../assets/images/date-picker.png";

type CustomDatePickerProps = {
  value: Date | undefined;
  onChange: (date: Date | null) => void;
  error?: boolean;
  errorMessage?: string | null;
  futureDate: boolean;
  errorExist?: boolean;
  dobDate?: boolean;
  readOnly?: boolean;
  startDate?: Date;
  iconStyles?: React.CSSProperties;
  disabled?: boolean;
  className?: string;
  height?: string;
};

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  error = false,
  errorMessage = null,
  futureDate,
  startDate,
  errorExist,
  dobDate,
  readOnly,
  iconStyles,
  disabled,
  className,
  height,
}) => {
  const [dateValue, setDateValue] = useState<Date | null>(value || null);

  //To prefill the date value
  useEffect(() => {
    setDateValue(value || null);
  }, [value]);

  /**
   * @description Handle date change will be called once the date is selected
   * @param date
   */
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const isValidDate = !isNaN(date.getTime());
      if (isValidDate) {
        onChange(new Date(date));
      } else {
        onChange(date);
      }
    } else {
      onChange(null);
    }
    setDateValue(date);
  };

   // Calculate the min date (3 years ago) and max date (100 years ago)
   const today = new Date();
   const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate() + 1);
   const maxDate = new Date(today.getFullYear() - 3, today.getMonth(), today.getDate());

  return (
    <Box
      className={`${className || ""} custom-datepicker`}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        position: "relative",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          // localeText={{
          //   fieldMonthPlaceholder: () => "",
          //   fieldYearPlaceholder: () => "",
          //   fieldDayPlaceholder: () => "",
          // }}
          localeText={{
            fieldMonthPlaceholder: () => "MMM",
          }}
          value={dateValue}
          onChange={handleDateChange}
          minDate={dobDate ? minDate : startDate ? startDate : undefined}
          maxDate={dobDate ? maxDate : futureDate ? maxDate || new Date() : undefined}
          format="dd MMM yyyy"
          sx={{
            width: "100%",
            fieldset: {
              border: "1px solid #cacaca",
              borderColor: errorExist ? "#E28619" : "#cacaca;",
              borderRadius: "5px",
              color: "#051B46",
              fontSize: 26,
            },
            "&:hover": {
              fieldset: {
                borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
              },
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: readOnly ? "#8A8A8A" : "#1859B4",
              },
            },
            "&:focus-within": {
              fieldset: {
                borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
              },
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
              },
            },
            "& input::placeholder": {
              fontSize: 16,
            },
            "& .MuiInputBase-input": {
              color: "#051b46",
              fontSize: 16,
              textAlign: "left",
              fontWeight: 400,
              cursor: readOnly ? "not-allowed" : "default",
            },
            input: {
              fontSize: 16,
              textAlign: "left",
              fontWeight: 400,
            },
            "& .MuiInputBase-root": {
              backgroundColor: readOnly ? "#f6f6f6" : "#fff",
              height: 40,
              gap: "8px",
              flexDirection: "row",
              width: "100%",
              cursor: readOnly ? "not-allowed" : "default",
              ...(className && { className }),
              ...(className && {
                backgroundColor: undefined,
                height: height,
              }),
            },
           " & .MuiInputAdornment-root": {
            borderLeft: "1.5px solid #cacaca !important" ,
           }
          }}
          slotProps={{
            textField: {
              autoFocus: false,
              error: !!error,
              helperText: errorMessage || null,
            },
            openPickerIcon: {
              component: () => (
                <img
                  width="24px"
                  height="24px"
                  src={datePickerIcon}
                  alt="DatePickerIcon"
                  style={iconStyles}
                  loading="lazy"
                />
              ),
            },
          }}
          readOnly={readOnly}
          disabled={disabled}
          className={className}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default CustomDatePicker;
